import nativeAuthService from './nativeAuthService';

export const passwordResetService = {
    // Request password reset email
    async requestReset(email) {
        try {
            const result = await nativeAuthService.requestPasswordReset(email);
            return result;
        } catch (error) {
            console.error('Password reset request failed:', error);
            return {
                success: false,
                error: 'Failed to request password reset'
            };
        }
    },

    // Verify reset token from URL
    async verifyToken(token) {
        try {
            const result = await nativeAuthService.verifyResetToken(token);
            return result;
        } catch (error) {
            console.error('Token verification failed:', error);
            return {
                success: false,
                error: 'Invalid or expired reset token'
            };
        }
    },

    // Reset password with token
    async resetPassword(token, newPassword, confirmPassword) {
        try {
            // Client-side validation
            if (!token) {
                return {
                    success: false,
                    error: 'Reset token is required'
                };
            }

            if (!newPassword) {
                return {
                    success: false,
                    error: 'New password is required'
                };
            }

            if (newPassword !== confirmPassword) {
                return {
                    success: false,
                    error: 'Passwords do not match'
                };
            }

            // Basic password strength check
            const passwordValidation = passwordResetService.validatePassword(newPassword);
            if (!passwordValidation.valid) {
                return {
                    success: false,
                    error: 'Password does not meet requirements',
                    details: passwordValidation.errors
                };
            }

            const result = await nativeAuthService.resetPassword(token, newPassword);
            return result;
        } catch (error) {
            console.error('Password reset failed:', error);
            return {
                success: false,
                error: 'Failed to reset password'
            };
        }
    },

    // Change password for authenticated user
    async changePassword(currentPassword, newPassword, confirmPassword) {
        try {
            // Client-side validation
            if (!currentPassword) {
                return {
                    success: false,
                    error: 'Current password is required'
                };
            }

            if (!newPassword) {
                return {
                    success: false,
                    error: 'New password is required'
                };
            }

            if (newPassword !== confirmPassword) {
                return {
                    success: false,
                    error: 'New passwords do not match'
                };
            }

            if (currentPassword === newPassword) {
                return {
                    success: false,
                    error: 'New password must be different from current password'
                };
            }

            // Basic password strength check
            const passwordValidation = passwordResetService.validatePassword(newPassword);
            if (!passwordValidation.valid) {
                return {
                    success: false,
                    error: 'Password does not meet requirements',
                    details: passwordValidation.errors
                };
            }

            const result = await nativeAuthService.changePassword(currentPassword, newPassword);
            return result;
        } catch (error) {
            console.error('Password change failed:', error);
            return {
                success: false,
                error: 'Failed to change password'
            };
        }
    },

    // Client-side password validation
    validatePassword(password) {
        const errors = [];
        const minLength = 8;

        if (!password) {
            errors.push('Password is required');
            return { valid: false, errors };
        }

        if (password.length < minLength) {
            errors.push(`Password must be at least ${minLength} characters long`);
        }

        // Optional: Add more complex validation rules
        // if (!/[A-Z]/.test(password)) {
        //     errors.push('Password must contain at least one uppercase letter');
        // }

        // if (!/[a-z]/.test(password)) {
        //     errors.push('Password must contain at least one lowercase letter');
        // }

        // if (!/\d/.test(password)) {
        //     errors.push('Password must contain at least one number');
        // }

        return { valid: errors.length === 0, errors };
    },

    // Extract token from URL parameters
    getTokenFromURL() {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('token');
    },

    // Clear URL parameters after successful reset
    clearURLParameters() {
        if (window.history && window.history.pushState) {
            const url = new URL(window.location);
            url.searchParams.delete('token');
            window.history.pushState({}, '', url);
        }
    },

    // Generate password strength indicator
    getPasswordStrength(password) {
        if (!password) {
            return { score: 0, text: 'Enter a password', class: 'weak' };
        }

        let score = 0;
        const checks = {
            length: password.length >= 8,
            lowercase: /[a-z]/.test(password),
            uppercase: /[A-Z]/.test(password),
            number: /\d/.test(password),
            special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
        };

        // Calculate score
        Object.values(checks).forEach(check => {
            if (check) score++;
        });

        // Bonus points for length
        if (password.length >= 12) score++;

        // Return strength assessment
        if (score < 2) {
            return { score, text: 'Weak', class: 'weak' };
        } else if (score < 4) {
            return { score, text: 'Fair', class: 'fair' };
        } else if (score < 5) {
            return { score, text: 'Good', class: 'good' };
        } else {
            return { score, text: 'Strong', class: 'strong' };
        }
    }
};